import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Languages, ArrowRight, Heart, Sparkles, Flame } from "lucide-react";

/**
 * LanguageEntryCard — Iter81.
 *
 * Hub entry-point widget for the language-learning mode. Two states:
 *  1. No active course → invite card with "Start learning" CTA.
 *  2. Has 1+ active courses → progress card showing target flag,
 *     XP, hearts, current unit/lesson, and a "Continue" CTA.
 *
 * Mounted on Hub between FriendLeaderboard and Vision-Scan upload.
 */
export default function LanguageEntryCard() {
  const [courses, setCourses] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/lang/progress")
      .then(({ data }) => { if (!cancelled) setCourses(data?.courses || []); })
      .catch(() => { if (!cancelled) setCourses([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;

  const active = (courses || [])[0];

  // Empty state — invite the user to start.
  if (!active) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="glass p-5 sm:p-6 relative overflow-hidden"
        data-testid="language-entry-empty"
      >
        <div className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-[#00FF9D]/12 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative flex items-start gap-4">
          <div className="h-11 w-11 grid place-items-center rounded-xl bg-[#00FF9D]/12 border border-[#00FF9D]/35 shrink-0">
            <Languages size={18} className="text-[#9bffcc]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-[#9bffcc] mb-1 flex items-center gap-1.5">
              Language learning
              <span className="text-[9px] font-mono-oak tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#00FF9D]/15 border border-[#00FF9D]/35 text-[#9bffcc]">
                NEW
              </span>
            </div>
            <h3 className="font-display text-lg sm:text-xl text-white tracking-tight leading-snug">
              Learn a new language, the gamified way
            </h3>
            <p className="text-[12.5px] text-white/55 mt-1 leading-relaxed">
              Spanish, French, Japanese and 9 more. Bite-sized lessons, hearts, streaks, gems — basic to advanced in your pocket.
            </p>
            <Link
              to="/learn-language"
              className="mt-3 inline-flex items-center gap-1.5 text-[12px] btn-primary px-4 py-2 rounded-full font-display font-semibold"
              data-testid="language-entry-cta"
            >
              <Sparkles size={11} /> Pick a language
              <ArrowRight size={11} />
            </Link>
          </div>
        </div>
      </motion.section>
    );
  }

  // Active course — show progress.
  const totalLessons = (active.units || []).reduce((sum, u) => sum + u.lessons.length, 0);
  const doneLessons = (active.completed || []).length;
  const pct = totalLessons ? Math.round((doneLessons / totalLessons) * 100) : 0;
  const unitTitle = active.units?.[active.current_unit]?.title || "—";

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass p-5 sm:p-6 relative overflow-hidden"
      data-testid="language-entry-active"
    >
      <div className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-[#00FF9D]/12 blur-3xl pointer-events-none" aria-hidden />
      <div className="relative flex items-start gap-4">
        <div className="h-11 w-11 grid place-items-center rounded-xl bg-[#00FF9D]/12 border border-[#00FF9D]/35 shrink-0">
          <span className="text-xl" aria-hidden>{active.target_flag}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-[#9bffcc] mb-1 flex items-center gap-1.5">
            Learning {active.target_name}
            <span className="text-[10px] text-white/45 font-mono-oak">· {active.level}</span>
          </div>
          <h3 className="font-display text-lg sm:text-xl text-white tracking-tight leading-snug truncate">
            {unitTitle} · Lesson {active.current_lesson + 1}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-white/55 font-mono-oak">
            <span className="inline-flex items-center gap-1">
              <Heart size={10} className="text-rose-400" fill="currentColor" /> {active.hearts}/{active.max_hearts}
            </span>
            <span className="inline-flex items-center gap-1">
              <Sparkles size={10} className="text-[#9bffcc]" /> {active.total_xp || 0} XP
            </span>
            <span>· {doneLessons}/{totalLessons} lessons</span>
          </div>
          <div className="h-1 w-full bg-white/8 rounded-full overflow-hidden mt-2.5">
            <div className="h-full bg-gradient-to-r from-[#00FF9D] to-[#0044FF]" style={{ width: `${pct}%` }} />
          </div>
          <Link
            to="/learn-language"
            className="mt-3 inline-flex items-center gap-1.5 text-[12px] btn-primary px-4 py-2 rounded-full font-display font-semibold"
            data-testid="language-entry-continue"
          >
            Continue learning <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </motion.section>
  );
}
